// iter116 — Admin Completion Correction Sheet.
// Lets Super Admins / Admins / Service Managers open a closed (or pending) job
// completion and correct closeout fields — work performed, checklist answers,
// quick equipment, review-request opt-in, invoice decision and payment
// disposition — WITHOUT a developer.
//
// Backend (already shipped iter113):
//   GET  /api/admin/job-completions/{id}              → full completion doc
//   PUT  /api/admin/job-completions/{id}              → manager edit (iter116 expanded)
//   GET  /api/admin/job-completions/{id}/edit-history → correction trail (iter116 new)
//
// Permissions: super_admin / admin / service_manager only (gate is enforced both
// in the parent that opens this sheet AND in the backend's require_role('admin')
// dependency).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Loader2, History, ShieldAlert, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INVOICE_STATUS_OPTIONS = [
  { key: '',                          label: '— Leave unchanged —' },
  { key: 'create_invoice',            label: 'Create Invoice Now' },
  { key: 'save_draft',                label: 'Save Draft Invoice' },
  { key: 'hsp_claim',                 label: 'Warranty Invoice (HSP)' },
  { key: 'nexflow_warranty',          label: 'NexFlow Warranty / Callback' },
  { key: 'return_visit_needed',       label: 'Return Visit Needed' },
  { key: 'deferred_unable_collect',   label: 'Deferred / Unable to Collect Today' },
];

const PAYMENT_DISPOSITION_OPTIONS = [
  { key: '',                          label: '— Leave unchanged —' },
  { key: 'paid_cash',                 label: 'Customer Paid — Cash' },
  { key: 'paid_check',                label: 'Customer Paid — Check' },
  { key: 'paid_other',                label: 'Customer Paid — Other (QB / Zelle / Venmo / etc.)' },
  { key: 'paid_card',                 label: 'Customer Paid — Card (manual record)' },
  { key: 'financing',                 label: 'Financing Used' },
  { key: 'payment_link_sent',         label: 'Payment Link Sent' },
  { key: 'deferred_unable_collect',   label: 'Deferred / Unable to Collect' },
  { key: 'no_balance_due',            label: 'No Customer Balance Due' },
];

const PAID_OTHER_METHODS = [
  'QuickBooks Pay / GoPayment',
  'External card terminal',
  'Zelle',
  'Venmo',
  'Cash App',
  'PayPal',
  'Already paid',
  'Manager approved',
  'Other',
];

const CORRECTION_REASONS = [
  { key: 'payment_method',         label: 'Payment method corrected' },
  { key: 'tech_error',             label: 'Tech entered wrong amount / info' },
  { key: 'customer_paid_after',    label: 'Customer paid after closeout' },
  { key: 'checklist_wrong',        label: 'Checklist answer was wrong' },
  { key: 'wrong_review_opt_in',    label: 'Review request opt-in was wrong' },
  { key: 'closed_too_early',       label: 'Completion closed too early' },
  { key: 'admin_cleanup',          label: 'Admin cleanup / housekeeping' },
  { key: 'other',                  label: 'Other' },
];

const SATISFACTION_OPTIONS = [
  { key: '',     label: '— Unchanged —' },
  { key: 'yes',  label: 'Yes' },
  { key: 'no',   label: 'No' },
  { key: 'na',   label: 'Not sure' },
];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtVal  = (v)   => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

const ALLOWED_ROLES = new Set(['super_admin', 'admin', 'service_manager']);
export const canCorrectCompletion = (user) => !!user && ALLOWED_ROLES.has(user.role);

const AdminCompletionEditSheet = ({ open, onOpenChange, completionId, onSaved }) => {
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [comp,     setComp]     = useState(null);
  const [history,  setHistory]  = useState([]);

  // Editable form state
  const [workPerformed,         setWorkPerformed]         = useState('');
  const [materialsUsed,         setMaterialsUsed]         = useState('');
  const [hoursWorked,           setHoursWorked]           = useState('');
  const [recommendedFollowups,  setRecommendedFollowups]  = useState('');
  const [customerSignatureName, setCustomerSignatureName] = useState('');
  const [customerSatisfied,     setCustomerSatisfied]     = useState('');   // tri-state via dropdown
  const [sendReviewRequest,     setSendReviewRequest]     = useState(false);
  const [quickEquipmentWHAge,   setQuickEquipmentWHAge]   = useState('');
  const [quickEquipmentWHSize,  setQuickEquipmentWHSize]  = useState('');
  const [quickEquipmentConcern, setQuickEquipmentConcern] = useState('');
  const [invoiceStatus,         setInvoiceStatus]         = useState('');
  const [pdDisposition,         setPdDisposition]         = useState('');
  const [pdAmount,              setPdAmount]              = useState('');
  const [pdMethodDetail,        setPdMethodDetail]        = useState('');
  const [pdReference,           setPdReference]           = useState('');
  const [pdNotes,               setPdNotes]               = useState('');
  const [correctionReason,      setCorrectionReason]      = useState('');
  const [editNote,              setEditNote]              = useState('');

  const load = useCallback(async () => {
    if (!completionId) return;
    setLoading(true);
    try {
      const [{ data: d }, { data: hist }] = await Promise.all([
        axios.get(`${API}/admin/job-completions/${completionId}`, { withCredentials: true }),
        axios.get(`${API}/admin/job-completions/${completionId}/edit-history`, { withCredentials: true }),
      ]);
      setComp(d);
      setHistory(Array.isArray(hist) ? hist : []);
      setWorkPerformed(d.work_performed || '');
      setMaterialsUsed(d.materials_used || '');
      setHoursWorked(d.hours_worked ?? '');
      setRecommendedFollowups(d.recommended_followups || '');
      setCustomerSignatureName(d.customer_signature_name || '');
      const bc = d.basic_checklist || {};
      const cs = bc.customer_satisfied;
      setCustomerSatisfied(typeof cs === 'string' ? cs : (cs === true ? 'yes' : cs === false ? 'no' : ''));
      setSendReviewRequest(!!d.send_review_request);
      const qe = d.quick_equipment || {};
      setQuickEquipmentWHAge(qe.water_heater_age || '');
      setQuickEquipmentWHSize(qe.water_heater_size || '');
      setQuickEquipmentConcern(qe.major_concern || '');
      setInvoiceStatus('');           // explicit "unchanged" — only set if admin picks a new one
      const pd = d.payment_disposition || {};
      setPdDisposition('');           // same — admin must explicitly pick to change
      setPdAmount(pd.amount ?? '');
      setPdMethodDetail(pd.payment_method_detail || '');
      setPdReference(pd.reference || '');
      setPdNotes(pd.notes || '');
      setCorrectionReason('');
      setEditNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load completion');
    }
    setLoading(false);
  }, [completionId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSave = async () => {
    if (!correctionReason) {
      toast.error('Pick a reason for correction so we have an audit trail.');
      return;
    }
    setSaving(true);
    const payload = { correction_reason: correctionReason };
    if (editNote.trim())   payload.edit_note = editNote.trim();
    if (workPerformed !== (comp?.work_performed || ''))  payload.work_performed = workPerformed;
    if (materialsUsed !== (comp?.materials_used || ''))  payload.materials_used = materialsUsed;
    if (String(hoursWorked) !== String(comp?.hours_worked ?? '')) {
      const h = parseFloat(hoursWorked);
      payload.hours_worked = Number.isFinite(h) ? h : null;
    }
    if (recommendedFollowups !== (comp?.recommended_followups || ''))  payload.recommended_followups = recommendedFollowups;
    if (customerSignatureName !== (comp?.customer_signature_name || '')) payload.customer_signature_name = customerSignatureName;

    // Basic checklist — preserve everything except customer_satisfied which the admin can flip.
    const existingBc = comp?.basic_checklist || {};
    const existingCs = typeof existingBc.customer_satisfied === 'string' ? existingBc.customer_satisfied : (existingBc.customer_satisfied === true ? 'yes' : existingBc.customer_satisfied === false ? 'no' : '');
    if (customerSatisfied && customerSatisfied !== existingCs) {
      payload.basic_checklist = { ...existingBc, customer_satisfied: customerSatisfied };
    }
    if (sendReviewRequest !== !!comp?.send_review_request) {
      payload.send_review_request = sendReviewRequest;
    }
    const existingQe = comp?.quick_equipment || {};
    if (
      (quickEquipmentWHAge   !== (existingQe.water_heater_age   || '')) ||
      (quickEquipmentWHSize  !== (existingQe.water_heater_size  || '')) ||
      (quickEquipmentConcern !== (existingQe.major_concern       || ''))
    ) {
      payload.quick_equipment = {
        ...existingQe,
        water_heater_age:  quickEquipmentWHAge,
        water_heater_size: quickEquipmentWHSize,
        major_concern:     quickEquipmentConcern,
      };
    }
    if (invoiceStatus)  payload.invoice_status = invoiceStatus;
    if (pdDisposition) {
      payload.payment_disposition = {
        disposition: pdDisposition,
        amount: pdAmount === '' ? null : parseFloat(pdAmount),
        payment_method_detail: pdMethodDetail.trim(),
        reference: pdReference.trim(),
        notes: pdNotes.trim(),
      };
    }
    try {
      const { data } = await axios.put(`${API}/admin/job-completions/${completionId}`, payload, { withCredentials: true });
      if (data?.changed === false) {
        toast.message('No changes detected.');
      } else {
        toast.success(`Completion corrected — ${data?.fields?.length || 0} field${data?.fields?.length === 1 ? '' : 's'} updated.`);
        onSaved && onSaved();
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto"
        data-testid="admin-completion-edit-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#D4AF37]" />
            Correct Job Completion
          </SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">
            Admin override. Every change is captured in the correction trail with your name + reason. Only edit what the technician got wrong.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-[#A3A3A3] mt-8" data-testid="admin-completion-edit-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading completion…
          </div>
        ) : !comp ? (
          <div className="text-[#A3A3A3] mt-8">Completion not found.</div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Customer</p><p className="text-white">{comp.customer_name || '—'}</p></div>
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Tech</p><p className="text-white">{comp.employee_name || comp.submitted_by_name || '—'}</p></div>
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Date</p><p className="text-white">{comp.date || '—'}</p></div>
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Status</p><p className="text-white">{(comp.status || '').replace('_', ' ')}</p></div>
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Submitted</p><p className="text-white">{fmtDate(comp.submitted_at)}</p></div>
              <div><p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Invoice</p><p className="text-white">{comp.invoice_status || comp.invoice_id ? (comp.invoice_status || 'linked') : '—'}</p></div>
            </div>

            <section className="space-y-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Work Notes</h3>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Work Performed</Label>
                <Textarea
                  data-testid="admin-edit-work-performed"
                  rows={3}
                  value={workPerformed}
                  onChange={(e) => setWorkPerformed(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Hours Worked</Label>
                  <Input
                    data-testid="admin-edit-hours-worked"
                    type="number"
                    inputMode="decimal"
                    value={hoursWorked}
                    onChange={(e) => setHoursWorked(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Signature Name</Label>
                  <Input
                    data-testid="admin-edit-customer-signature"
                    value={customerSignatureName}
                    onChange={(e) => setCustomerSignatureName(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Materials Used</Label>
                <Textarea
                  data-testid="admin-edit-materials-used"
                  rows={2}
                  value={materialsUsed}
                  onChange={(e) => setMaterialsUsed(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Recommended Follow-ups</Label>
                <Textarea
                  data-testid="admin-edit-followups"
                  rows={2}
                  value={recommendedFollowups}
                  onChange={(e) => setRecommendedFollowups(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                />
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Checklist & Customer Experience</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Satisfied</Label>
                  <Select value={customerSatisfied || 'unchanged'} onValueChange={(v) => setCustomerSatisfied(v === 'unchanged' ? '' : v)}>
                    <SelectTrigger data-testid="admin-edit-satisfied" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {SATISFACTION_OPTIONS.map((o) => (
                        <SelectItem key={o.key || 'unchanged'} value={o.key || 'unchanged'} className="text-white">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                    <input
                      data-testid="admin-edit-review-opt-in"
                      type="checkbox"
                      checked={sendReviewRequest}
                      onChange={(e) => setSendReviewRequest(e.target.checked)}
                      className="w-4 h-4 accent-[#D4AF37]"
                    />
                    Send Google review request
                  </label>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Quick Equipment Notes</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Water Heater Age</Label>
                  <Input
                    data-testid="admin-edit-wh-age"
                    value={quickEquipmentWHAge}
                    onChange={(e) => setQuickEquipmentWHAge(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Water Heater Size</Label>
                  <Input
                    data-testid="admin-edit-wh-size"
                    value={quickEquipmentWHSize}
                    onChange={(e) => setQuickEquipmentWHSize(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Major Concern</Label>
                  <Input
                    data-testid="admin-edit-concern"
                    value={quickEquipmentConcern}
                    onChange={(e) => setQuickEquipmentConcern(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Invoice Decision</h3>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Override Invoice Status</Label>
                <Select value={invoiceStatus || 'unchanged'} onValueChange={(v) => setInvoiceStatus(v === 'unchanged' ? '' : v)}>
                  <SelectTrigger data-testid="admin-edit-invoice-status" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {INVOICE_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.key || 'unchanged'} value={o.key || 'unchanged'} className="text-white">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#525252] mt-1">Current: <span className="text-white">{comp.invoice_status || '—'}</span></p>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Payment Disposition</h3>
              <p className="text-[10px] text-[#525252]">
                Current: <span className="text-white">{comp.payment_disposition?.disposition || '—'}</span>
                {comp.payment_disposition?.payment_method_detail ? <> · {comp.payment_disposition.payment_method_detail}</> : null}
                {comp.payment_disposition?.amount ? <> · ${Number(comp.payment_disposition.amount).toFixed(2)}</> : null}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Override Disposition</Label>
                  <Select value={pdDisposition || 'unchanged'} onValueChange={(v) => setPdDisposition(v === 'unchanged' ? '' : v)}>
                    <SelectTrigger data-testid="admin-edit-pd-disposition" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {PAYMENT_DISPOSITION_OPTIONS.map((o) => (
                        <SelectItem key={o.key || 'unchanged'} value={o.key || 'unchanged'} className="text-white">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Amount</Label>
                  <Input
                    data-testid="admin-edit-pd-amount"
                    type="number"
                    inputMode="decimal"
                    value={pdAmount}
                    onChange={(e) => setPdAmount(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
              </div>
              {pdDisposition === 'paid_other' && (
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Method Detail (required for Paid — Other)</Label>
                  <Select value={pdMethodDetail || 'pick'} onValueChange={(v) => setPdMethodDetail(v === 'pick' ? '' : v)}>
                    <SelectTrigger data-testid="admin-edit-pd-method-detail" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="Pick a method" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="pick" className="text-white">— Pick a method —</SelectItem>
                      {PAID_OTHER_METHODS.map((m) => (
                        <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reference (auth code / last 4 / check #)</Label>
                  <Input
                    data-testid="admin-edit-pd-reference"
                    value={pdReference}
                    onChange={(e) => setPdReference(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Payment Notes</Label>
                  <Input
                    data-testid="admin-edit-pd-notes"
                    value={pdNotes}
                    onChange={(e) => setPdNotes(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3">
              <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Reason for Correction (required)</h3>
              <Select value={correctionReason || 'pick'} onValueChange={(v) => setCorrectionReason(v === 'pick' ? '' : v)}>
                <SelectTrigger data-testid="admin-edit-reason" className="bg-[#141414] border-[#262626] text-white"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="pick" className="text-white">— Pick a reason —</SelectItem>
                  {CORRECTION_REASONS.map((r) => (
                    <SelectItem key={r.key} value={r.key} className="text-white">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                data-testid="admin-edit-edit-note"
                rows={2}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Optional context (visible in audit trail)"
                className="bg-[#141414] border-[#262626] text-white"
              />
            </section>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#262626]">
              <Button variant="outline" data-testid="admin-edit-cancel" onClick={() => onOpenChange(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
              <Button
                data-testid="admin-edit-save"
                onClick={handleSave}
                disabled={saving || !correctionReason}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Correction
              </Button>
            </div>

            <section className="space-y-2" data-testid="admin-edit-history-panel">
              <h3 className="text-[#A3A3A3] text-xs uppercase tracking-wider font-bold flex items-center gap-2">
                <History className="w-3 h-3" /> Correction Trail ({history.length})
              </h3>
              {history.length === 0 ? (
                <p className="text-[10px] text-[#525252]">No prior corrections logged.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.id} data-testid={`admin-edit-history-row-${h.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white font-medium">{h.changed_by_name || '—'} <span className="text-[#525252]">({h.changed_by_role || ''})</span></span>
                        <span className="text-[#A3A3A3]">{fmtDate(h.changed_at)}</span>
                      </div>
                      {h.correction_reason && <p className="text-[#D4AF37] mt-1">Reason: {CORRECTION_REASONS.find((r) => r.key === h.correction_reason)?.label || h.correction_reason}</p>}
                      {h.edit_note && <p className="text-[#A3A3A3] italic mt-1">&ldquo;{h.edit_note}&rdquo;</p>}
                      {Array.isArray(h.diffs) && h.diffs.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-[#A3A3A3]">
                          {h.diffs.map((d, i) => (
                            <li key={i} className="leading-tight">
                              <span className="text-[#737373]">{d.field}:</span>{' '}
                              <span className="text-red-300 line-through">{fmtVal(d.previous)}</span>{' '}
                              <span className="text-[#525252]">→</span>{' '}
                              <span className="text-green-300">{fmtVal(d.new)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AdminCompletionEditSheet;
